import { useEffect, useState } from 'react';
import {
    analyticsApi,
    buildAnalyticsQuery,
    type AnalyticsCohorts,
    type AnalyticsEvent,
    type AnalyticsFilters,
} from '../../services/analytics';
import { EventSelect } from './EventSelect';
import { DEFAULT_EVENT_ID, eventOptions } from './events';
import { DASH } from './format';
import { cohortCell, cohortSubtitle, heatColor, retentionHeaders } from './retention';
import styles from './EventCohorts.module.css';

interface EventCohortsProps {
    filters: AnalyticsFilters;
    /** Lista de eventos vinda de meta. */
    events?: AnalyticsEvent[];
    /** Falso enquanto o periodo personalizado esta incompleto: nada e consultado. */
    enabled: boolean;
    /** Muda quando a pessoa pede "tentar novamente" no painel. */
    refreshKey: number;
}

interface Settled {
    key: string;
    error: string | null;
}

export function EventCohorts({ filters, events, enabled, refreshKey }: EventCohortsProps) {
    const [event, setEvent] = useState(DEFAULT_EVENT_ID);
    const [data, setData] = useState<AnalyticsCohorts | null>(null);
    const [settled, setSettled] = useState<Settled | null>(null);
    const [retry, setRetry] = useState(0);

    const options = eventOptions(events);
    const requestKey = enabled ? `${buildAnalyticsQuery(filters)}|${event}#${refreshKey}.${retry}` : null;

    useEffect(() => {
        if (!requestKey) return;
        const controller = new AbortController();
        analyticsApi
            .cohorts(filters, event, controller.signal)
            .then((d) => {
                setData(d);
                setSettled({ key: requestKey, error: null });
            })
            .catch((e: unknown) => {
                if (controller.signal.aborted) return;
                setSettled({
                    key: requestKey,
                    error: e instanceof Error ? e.message : 'Não foi possível carregar as coortes.',
                });
            });
        return () => controller.abort();
        // requestKey resume filtros, evento e contadores de nova tentativa.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    const loading = requestKey !== null && settled?.key !== requestKey;
    const error = settled && settled.key === requestKey ? settled.error : null;
    const headers = data ? retentionHeaders(data.months) : [];

    return (
        <section className={`${styles.card} ${loading ? styles.loading : ''}`} aria-busy={loading}>
            <header className={styles.header}>
                <div className={styles.heading}>
                    <h4 className={styles.title}>Coortes mensais do evento</h4>
                    <p className={styles.subtitle}>{data ? cohortSubtitle(data.months.length) : DASH}</p>
                </div>
                <EventSelect label="Evento neste visual" value={event} options={options} onChange={setEvent} />
            </header>

            {error && (
                <div className={styles.error} role="alert">
                    <span>{error}</span>
                    <button type="button" className={styles.retry} onClick={() => setRetry((n) => n + 1)}>
                        Tentar novamente
                    </button>
                </div>
            )}

            {!error && !data && <p className={styles.message}>Carregando...</p>}

            {data && (
                <div className={styles.scroll}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th scope="col" className={`${styles.safraCol} ${styles.left}`}>
                                    Safra
                                </th>
                                <th scope="col" className={styles.sizeCol}>
                                    Contas
                                </th>
                                {headers.map((h) => (
                                    <th key={h} scope="col">
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map((row) => (
                                <tr key={row.month}>
                                    <th scope="row" className={`${styles.safra} ${styles.left}`}>
                                        {row.month.slice(0, 7)}
                                    </th>
                                    <td className={styles.size}>{row.size}</td>
                                    {data.months.map((m, i) => {
                                        const cell = cohortCell(row.cols.find((c) => c.offset === i));
                                        return (
                                            <td key={m}>
                                                {cell ? (
                                                    <div
                                                        className={styles.heat}
                                                        style={{ backgroundColor: heatColor(cell.intensity) }}
                                                    >
                                                        <strong>{cell.count}</strong>
                                                        <span>{cell.pct}</span>
                                                    </div>
                                                ) : (
                                                    <div className={`${styles.heat} ${styles.empty}`}>{'\u2014'}</div>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {data.rows.length === 0 && <p className={styles.message}>Sem safras no período.</p>}
                </div>
            )}
        </section>
    );
}
